import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, Alert, Checkbox, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  alpha, useTheme, LinearProgress, FormControl, InputLabel, Select, MenuItem,
  TextField, InputAdornment, IconButton,
} from '@mui/material';
import { HowToReg, LocalShipping, CheckCircle, Search, Clear } from '@mui/icons-material';

import { HandoverPreviewRow, HandoverRowResult } from '../../services/admissions/handoverService';
import { useHandover, classNameOf } from '../admissions/handover/useHandover';
import { HandoverSectionSelect, AssignSectionMenu } from '../admissions/handover/HandoverSectionPicker';
import HandoverConfirmDialog from '../admissions/handover/HandoverConfirmDialog';
import { admissionCycleService } from '../../services/admissions/admissionCycleService';
import { AdmissionCycle } from '../../types/admissions';
import { Session, SessionClass } from '../../types/sessions';
import TableRowSkeletons from '../common/TableRowSkeletons';
import HandoverApplicantName from '../admissions/handover/HandoverApplicantName';

/**
 * EnrollFromAdmissionsDialog — convenience entry point into the admission →
 * enrollment handover from inside the Enrollments module. Backend contract is
 * identical to the dedicated Admissions → Enrollment handover page:
 *
 *   - `handoverService.preview()` returns every Confirmed application across
 *     cycles with ready/blocked status + blockers[].
 *   - `handoverService.batch(ids)` runs the per-row transactional handover.
 *
 * The target session and class are NOT picked here — each confirmed
 * applicant already carries the cycle and class they were offered, and the
 * operator shouldn't retarget them mid-handover (it would violate the merit
 * decision). Only the section is chosen, when the class has sections.
 */

interface EnrollFromAdmissionsDialogProps {
  open: boolean;
  /** Unused — kept for drop-in compatibility with the previous dialog shape. */
  activeSession?: Session | null;
  /** Unused — see above. */
  classes?: SessionClass[];
  /** Unused — see above. */
  defaultClassId?: string;
  /** Unused — see above. */
  defaultSectionInstanceId?: string | null;
  onClose: () => void;
  onEnrolled: () => void;
}

const EnrollFromAdmissionsDialog: React.FC<EnrollFromAdmissionsDialogProps> = ({
  open, onClose, onEnrolled,
}) => {
  const theme = useTheme();

  const [cycles, setCycles] = useState<AdmissionCycle[]>([]);
  const [cycleFilter, setCycleFilter] = useState('');
  const [results, setResults] = useState<HandoverRowResult[] | null>(null);
  // The rows that were run — the preview reloads and drops enrolled ones.
  const [ranRows, setRanRows] = useState<HandoverPreviewRow[]>([]);
  const {
    rows, summary, loading, searchInput, setSearchInput, search,
    visibleRows, readyRows, blockedRows, selected, allReadySelected, someReadySelected, toggleAll, toggleOne,
    sectionOf, setSection, setSectionForSelected, sectionedClasses, missingSection, selectionSummary, running, run,
  } = useHandover({ cycleId: cycleFilter, enabled: open });
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Load cycles for the dropdown (Open + Closed only — Draft has nothing to
  // hand over, Archived is read-only).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await admissionCycleService.list({ limit: 100 });
        if (!cancelled) {
          setCycles(res.items.filter(c => c.status === 'Open' || c.status === 'Closed'));
        }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Back to the list each time the dialog opens.
  useEffect(() => { if (open) setResults(null); }, [open]);

  const commit = async () => {
    const out = await run();
    setConfirmOpen(false);
    if (!out) return;
    setRanRows(out.ranRows);
    setResults(out.response.results);
    onEnrolled();
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={running ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <HowToReg color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            {results ? 'Handover complete' : 'Enroll from admissions'}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {results
            ? 'Per-application results below.'
            : 'Confirmed applicants ready to become enrolled students. Each row runs in its own transaction so one bad row never kills the batch.'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Post-handover results view */}
        {results ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={1}>
              {results.map((r, i) => (
                <Paper
                  key={`${r.applicationId}-${i}`}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    borderColor: r.outcome === 'failed' ? 'error.main' : 'divider',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {ranRows.find(x => x.applicationId === r.applicationId)?.name || 'Applicant'}
                      </Typography>
                      {r.reason && (
                        <Typography
                          variant="caption"
                          color={r.outcome === 'failed' ? 'error.main' : 'text.secondary'}
                        >
                          {r.reason}
                        </Typography>
                      )}
                      {r.outcome === 'enrolled' && r.grNumber && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          GR {r.grNumber} · Roll {r.rollNumber}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      icon={r.outcome === 'enrolled' ? <CheckCircle sx={{ fontSize: 14 }} /> : undefined}
                      label={r.outcome}
                      color={
                        r.outcome === 'enrolled' ? 'success' :
                        r.outcome === 'failed'   ? 'error'   : 'default'
                      }
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : (
          <>
            {/* Toolbar: cycle filter + search + summary */}
            <Box sx={{
              px: 2, py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.text.primary, 0.015),
            }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Cycle</InputLabel>
                  <Select
                    label="Cycle"
                    value={cycleFilter}
                    onChange={(e) => setCycleFilter(e.target.value)}
                  >
                    <MenuItem value=""><em>All cycles</em></MenuItem>
                    {cycles.map(c => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                        <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>· {c.status}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="Search by name, application no. or class…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchInput('')}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                  sx={{ flex: 1, minWidth: 220 }}
                />
                {summary && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" variant="outlined" label={`${summary.ready} ready`} color="success" sx={{ height: 22, fontSize: 11 }} />
                    {summary.blocked > 0 && (
                      <Chip size="small" variant="outlined" label={`${summary.blocked} blocked`} color="warning" sx={{ height: 22, fontSize: 11 }} />
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>

            {/* Table */}
            {loading && rows.length > 0 && <LinearProgress sx={{ height: 2 }} />}
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                      <Checkbox
                        size="small"
                        checked={allReadySelected}
                        indeterminate={someReadySelected}
                        onChange={toggleAll}
                        disabled={readyRows.length === 0 || loading || running}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper' }}>Applicant</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper' }}>Class</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper' }}>Section</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && rows.length === 0 ? (
                    <TableRowSkeletons columns={5} rows={5} />
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <LocalShipping sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                            Nothing to hand over right now
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Applications become eligible when they reach Confirmed with family resolved and enrollment confirmation recorded.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">No match for "{search}".</Typography>
                        <Button size="small" sx={{ mt: 1 }} onClick={() => setSearchInput('')}>Clear search</Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {readyRows.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0.75, bgcolor: alpha(theme.palette.success.main, 0.04) }}>
                            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: theme.palette.success.main }}>
                              Ready to enroll ({readyRows.length})
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {readyRows.map(r => (
                        <TableRow
                          key={r.applicationId}
                          hover
                          sx={{ cursor: running ? 'default' : 'pointer' }}
                          onClick={() => !running && toggleOne(r.applicationId)}
                        >
                          <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={selected.has(r.applicationId)}
                              onChange={() => toggleOne(r.applicationId)}
                              disabled={running}
                            />
                          </TableCell>
                          <TableCell>
                            <HandoverApplicantName row={r} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {classNameOf(r) || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <HandoverSectionSelect
                              row={r}
                              value={sectionOf[r.applicationId]}
                              onChange={(id) => setSection(r.applicationId, id)}
                              disabled={running}
                              required={selected.has(r.applicationId)}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label="Ready" color="success" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                          </TableCell>
                        </TableRow>
                      ))}

                      {blockedRows.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0.75, bgcolor: alpha(theme.palette.warning.main, 0.04) }}>
                            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: theme.palette.warning.main }}>
                              Blocked ({blockedRows.length})
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {blockedRows.map(r => (
                        <TableRow key={r.applicationId} sx={{ opacity: 0.7 }}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" disabled />
                          </TableCell>
                          <TableCell>
                            <HandoverApplicantName row={r} />
                            <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5 }}>
                              {r.blockers.join(' · ')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {classNameOf(r) || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell>
                            <Chip size="small" label="Blocked" color="warning" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {blockedRows.length > 0 && !loading && (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 0, borderLeft: 0, borderRight: 0, borderBottom: 0 }}>
                <Typography variant="caption">
                  Blocked rows need their preconditions cleared on the application's Family and Decision tabs before they can be enrolled.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {results ? (
          <Button onClick={onClose} variant="contained" disableElevation>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={running}>Cancel</Button>
            <Box sx={{ flex: 1 }} />
            <AssignSectionMenu classes={sectionedClasses} onAssign={setSectionForSelected} disabled={running} />
            <Button
              variant="contained"
              color="success"
              disableElevation
              startIcon={<CheckCircle fontSize="small" />}
              onClick={() => setConfirmOpen(true)}
              disabled={running || selected.size === 0 || missingSection > 0}
            >
              {running ? 'Running…' : missingSection > 0 ? `Choose a section for ${missingSection}` : `Enroll selected (${selected.size})`}
            </Button>
          </>
        )}
      </DialogActions>
      <HandoverConfirmDialog
        open={confirmOpen}
        summary={selectionSummary}
        running={running}
        onClose={() => setConfirmOpen(false)}
        onConfirm={commit}
      />
    </Dialog>
  );
};

export default EnrollFromAdmissionsDialog;
